/**
 * 类型和模板上下文管理
 *
 * 这个文件负责管理技能组件的类型和模板信息，包括：
 * - 技能组件类型管理
 * - 组件模板管理
 * - React Flow 实例管理
 * - 节点删除功能
 */

import { createContext, ReactNode, useEffect, useRef, useState } from 'react'
import { Node } from 'reactflow'
import { APIKindType } from '../types/api'
import { typesContextType } from '../types/typesContext'

/**
 * 类型上下文的初始值
 */
const initialValue: typesContextType = {
  reactFlowInstance: null,
  setReactFlowInstance: () => {},
  deleteNode: () => {},
  types: {},
  setTypes: () => {},
  templates: {},
  setTemplates: () => {},
  data: {},
  setData: () => {},
  loadAllData: () => {}
}

// 创建类型上下文
export const typesContext = createContext<typesContextType>(initialValue)

/**
 * 类型提供者组件
 *
 * @param children - 子组件
 * @returns 提供类型和模板相关状态和方法的上下文提供者
 */
export function TypesProvider({ children }: { children: ReactNode }) {
  const [types, setTypes] = useState({})
  const [reactFlowInstance, setReactFlowInstance] = useState(null)
  const [templates, setTemplates] = useState({})
  const [data, setData] = useState({})

  // 用来控制只调用一次 /all接口
  const isLoaded = useRef(false)

  useEffect(() => {
    const result = []
    updateTypes(result)
  }, [])

  /**
   * 删除节点
   * @param idx - 要删除的节点ID
   */
  function deleteNode(idx: string) {
    if (!reactFlowInstance) return
    // 从节点列表中删除指定节点
    reactFlowInstance.setNodes(reactFlowInstance.getNodes().filter((n: Node) => n.id !== idx))
    // 删除与该节点相关的所有边
    reactFlowInstance.setEdges(reactFlowInstance.getEdges().filter(ns => ns.source !== idx && ns.target !== idx))
  }

  /**
   * 更新类型和模板数据
   * @param result - 从API获取的结果数据
   */
  function updateTypes(result) {
    setData(result)

    // 设置模板数据，将所有类别的模板合并到一个对象中
    setTemplates(
      Object.keys(result).reduce((acc, curr) => {
        Object.keys(result[curr]).forEach((c: keyof APIKindType) => {
          acc[c] = result[curr][c]
        })
        return acc
      }, {})
    )

    // 设置类型映射，将类型名称映射到其所属的类别
    setTypes(
      // 反转键的顺序，避免工具世界重叠
      Object.keys(result)
        .reverse()
        .reduce((acc, curr) => {
          Object.keys(result[curr]).forEach((c: keyof APIKindType) => {
            acc[c] = curr
            // 同时添加基类到累加器中
            result[curr][c].base_classes?.forEach(b => {
              acc[b] = curr
            })
          })
          return acc
        }, {})
    )
  }

  /**
   * 加载所有数据
   * 注意：目前该功能被注释掉，因为接口调用被禁用
   */
  function loadAllData() {
    // if (!isLoaded.current) {
    //   getAll().then(res => {
    //     updateTypes(res)
    //   })
    //   isLoaded.current = true
    // }
    isLoaded.current = true
  }

  return (
    <typesContext.Provider
      value={{
        types,
        setTypes,
        reactFlowInstance,
        setReactFlowInstance,
        deleteNode,
        setTemplates,
        templates,
        data,
        setData,
        loadAllData
      }}
    >
      {children}
    </typesContext.Provider>
  )
}
